// Dreamlink Studio · Localization offering — studio.dreamlink.global/localization
//
// Dreamlink's own localization + global distribution capability (engine: Aview,
// white-labeled). Light cream editorial. Copy voice: no em-dashes, "AI" is not
// used as a buzzword. Inquiries route with service=localization.

import { Link } from 'react-router-dom';
import { PublicNav } from '../../components/nav/PublicNav';
import { Pegasus } from '../../components/DreamlinkMark';

const MAROON = '#590b11';
const MAROON_DEEP = '#3d0810';
const CREAM = '#F5F2EE';
const INK = '#262626';
const BROWN = '#6F5C53';
const LINE = 'rgba(38,38,38,0.12)';
const SERIF = "'Cormorant Garamond', Georgia, serif";

const LOOP = ['Localize', 'Distribute', 'Measure per market', 'Decide what is next'];

const RECEIVE = [
  ['Dubbed videos', 'Full dubs per language, voice-matched to the original.'],
  ['Localized audio tracks', 'Multi-track audio so one upload plays in every language.'],
  ['Subtitles and captions', 'Timed, accurate subtitle files for each market.'],
  ['Titles and metadata', 'Titles, descriptions and tags written for how each market searches.'],
  ['Thumbnails', 'Market-appropriate packaging, not just translated text.'],
  ['Regional channels', 'Dedicated localized channels where a native presence wins.'],
  ['Shorts and clips', 'Short-form cut-downs that feed discovery into long-form.'],
  ['Publishing', 'We publish into your channels and platforms for you.'],
  ['Per-market analytics', 'Watch time, retention and revenue, broken out by language.'],
];

const LAYERS = [
  ['Audio', 'Source narration becomes a native-voice dub, with the emotion preserved.'],
  ['Title', 'One headline becomes a title written for how that market searches.'],
  ['Thumbnail', 'One frame is redesigned for the local feed.'],
  ['Metadata', 'English tags become tags and descriptions in-language.'],
];

const QUALITY = [
  'Timing alignment and pacing integrity',
  'Pronunciation accuracy and intelligibility',
  'Tone and emotional consistency with the source',
  'Brand safety and content suitability',
  'Platform and streaming policy compliance',
];

export function Localization() {
  return (
    <div style={{ background: CREAM, color: INK, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
      <PublicNav theme="blue" />

      {/* Hero */}
      <header style={{ maxWidth: 980, margin: '0 auto', padding: '140px 28px 56px' }}>
        <div style={{ fontSize: 12, letterSpacing: '0.32em', textTransform: 'uppercase', color: BROWN, marginBottom: 18 }}>
          Dreamlink Studio · Localization
        </div>
        <h1 style={{ fontFamily: SERIF, fontWeight: 300, fontSize: 'clamp(40px, 6vw, 72px)', lineHeight: 1.04, letterSpacing: '-0.01em', margin: 0 }}>
          Your content, in every language your <span style={{ fontStyle: 'italic', color: MAROON }}>audience speaks</span>.
        </h1>
        <p style={{ fontSize: 19, color: '#4D3C3A', maxWidth: 660, margin: '24px 0 0', lineHeight: 1.6 }}>
          We localize the videos you already own and distribute them across platforms and markets, then use the results to decide what to localize next. You hand over a library, you stay in approval, and we run the operation end to end.
        </p>
        <div style={{ marginTop: 34 }}>
          <Link
            to="/inquire?service=localization"
            style={{ display: 'inline-block', background: MAROON, color: CREAM, padding: '15px 30px', borderRadius: 4, fontSize: 15, letterSpacing: '0.04em', fontWeight: 500 }}
          >
            Localize your library
          </Link>
        </div>
      </header>

      {/* The loop */}
      <section style={{ maxWidth: 980, margin: '0 auto', padding: '0 28px 64px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {LOOP.map((step, i) => (
            <span key={step} style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <span style={{ background: '#FFFFFF', border: `0.5px solid ${LINE}`, padding: '10px 16px', fontSize: 14, letterSpacing: '0.04em' }}>{step}</span>
              {i < LOOP.length - 1 ? <span style={{ color: BROWN }} aria-hidden>{'→'}</span> : null}
            </span>
          ))}
        </div>
        <p style={{ fontSize: 15, color: BROWN, marginTop: 16, maxWidth: 620 }}>
          It compounds as you run it. Every market's results sharpen the next decision, so the system gets better the more you use it.
        </p>
      </section>

      {/* What you receive */}
      <section style={{ maxWidth: 1040, margin: '0 auto', padding: '0 28px 72px' }}>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 'clamp(28px, 3.6vw, 40px)', color: MAROON, margin: '0 0 8px' }}>
          Everything needed to go live, delivered for you.
        </h2>
        <p style={{ fontSize: 15, color: BROWN, margin: '0 0 28px' }}>One source becomes a working presence in every market that matters.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
          {RECEIVE.map(([h, p]) => (
            <div key={h} style={{ background: '#FFFFFF', border: `0.5px solid ${LINE}`, padding: '24px 26px' }}>
              <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 22, margin: '0 0 8px', color: INK }}>{h}</h3>
              <p style={{ fontSize: 14.5, color: '#4D3C3A', lineHeight: 1.6, margin: 0 }}>{p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* One source, every layer rebuilt */}
      <section style={{ background: MAROON_DEEP, color: CREAM }}>
        <div style={{ maxWidth: 1040, margin: '0 auto', padding: '72px 28px' }}>
          <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 'clamp(28px, 3.6vw, 40px)', margin: '0 0 8px' }}>
            One source, every layer rebuilt per market.
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(245,242,238,0.75)', margin: '0 0 32px', maxWidth: 640 }}>
            A video is produced once. We rebuild every layer that decides how it performs in a new market, so it reads as native everywhere, not subtitled.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
            {LAYERS.map(([h, p]) => (
              <div key={h} style={{ borderTop: '1px solid rgba(245,242,238,0.25)', paddingTop: 16 }}>
                <h3 style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 400, fontSize: 24, margin: '0 0 8px' }}>{h}</h3>
                <p style={{ fontSize: 14, color: 'rgba(245,242,238,0.78)', lineHeight: 1.6, margin: 0 }}>{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality */}
      <section style={{ maxWidth: 980, margin: '0 auto', padding: '72px 28px' }}>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 'clamp(28px, 3.6vw, 40px)', color: MAROON, margin: '0 0 8px' }}>
          Built for broadcast, not demos.
        </h2>
        <p style={{ fontSize: 15, color: BROWN, margin: '0 0 24px', maxWidth: 660 }}>
          Streaming platforms have near-zero tolerance for timing drift, audio glitches, or policy violations. Quality is enforced on every asset before anything is distributed, automatically where we are confident and by a person where judgment is needed.
        </p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {QUALITY.map((q) => (
            <li key={q} style={{ position: 'relative', padding: '12px 0 12px 24px', borderBottom: `0.5px solid ${LINE}`, fontSize: 15.5 }}>
              <span style={{ position: 'absolute', left: 0, top: 18, width: 7, height: 7, border: `1px solid ${MAROON}`, transform: 'rotate(45deg)' }} />
              {q}
            </li>
          ))}
        </ul>
      </section>

      {/* CTA */}
      <section style={{ maxWidth: 980, margin: '0 auto', padding: '0 28px 96px', textAlign: 'center' }}>
        <h2 style={{ fontFamily: SERIF, fontWeight: 300, fontSize: 'clamp(30px, 4vw, 48px)', color: INK, margin: '0 0 20px' }}>
          New revenue from content you <span style={{ fontStyle: 'italic', color: MAROON }}>already own</span>.
        </h2>
        <Link
          to="/inquire?service=localization"
          style={{ display: 'inline-block', background: MAROON, color: CREAM, padding: '16px 34px', borderRadius: 4, fontSize: 15, letterSpacing: '0.04em', fontWeight: 500 }}
        >
          Start a localization project
        </Link>
      </section>

      <footer style={{ borderTop: `0.5px solid ${LINE}`, padding: '36px 28px', textAlign: 'center', color: BROWN, fontSize: 13 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 8, color: MAROON }}>
          <Pegasus size={16} color={MAROON} />
          <span style={{ fontFamily: SERIF, letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 14 }}>Dreamlink Studio</span>
        </div>
        <div>studio.dreamlink.global · A Dreamlink Global company</div>
      </footer>
    </div>
  );
}
